import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import MetricTooltip from './MetricTooltip';
import METRIC_DEFINITIONS from './metricDefinitions';
import { ANALYTICS_CHART_TICK } from './analyticsTypography';
import { ANALYTICS_RED, analyticsRedAlpha } from './analyticsColors';

const SectionHeader = ({ children }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: { xs: 3, sm: 3.5 }, mb: { xs: 1, sm: 1.25 } }}>
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'rgba(0,0,0,0.5)',
      }}
    >
      {children}
    </Typography>
    <MetricTooltip
      {...METRIC_DEFINITIONS.platformUsage}
      iconSize={12}
      iconColor="rgba(0,0,0,0.3)"
      srLabel="About Platform Usage"
    />
  </Stack>
);

/**
 * PlatformUsageSection
 *
 * Two parts, admin-gated (rendered only when monthlySummary data is available):
 *
 * A. Usage gauge card — current month's successful generations with an
 *    optional progress bar toward monthlyGenerationLimit. If no limit is
 *    configured for the partner, shows the raw count only.
 *
 * B. Monthly trend bar chart — last 6 months of successful generations
 *    (Recharts BarChart). Failures shown as a secondary muted bar.
 *    Recharts is already in the codebase (package.json ^3.8.1).
 *
 * Data: GET /api/v2/log/monthly-summary?partnerId=&from=&to=
 * Response: [{ month: "YYYY-MM", successful, failed, total, partnerTitle }]
 *
 * monthlyGenerationLimit: from partnerPackage.monthlyGenerationLimit on
 * the partner object returned by GET /api/v2/partners/:id.
 */

/** Format a "YYYY-MM" string to a short month label, e.g. "Jan", "Feb". */
const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', {
    month: 'short',
  });
};

// Section header styling lives in <SectionHeader/> above so the metric
// tooltip ships alongside it consistently.

const CARD_SX = {
  borderRadius: '14px',
  border: '1px solid rgba(0,0,0,0.06)',
  bgcolor: '#fff',
  p: { xs: 1.75, sm: 2 },
};

/** Custom tooltip for the bar chart — plain, minimal, matches the page style. */
const UsageTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const successful = payload.find((p) => p.dataKey === 'successful')?.value ?? 0;
  const failed = payload.find((p) => p.dataKey === 'failed')?.value ?? 0;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '8px',
        p: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.6)' }}>
        {successful} generated
        {failed > 0 && ` · ${failed} failed`}
      </Typography>
    </Box>
  );
};

const PlatformUsageSection = ({ monthlySummary = [], monthlyGenerationLimit = null, hasRequestedData = false }) => {
  if (!hasRequestedData) return null;

  // Sort ascending by month for the chart and pick the current month entry.
  const sorted = Array.isArray(monthlySummary)
    ? [...monthlySummary]
        .filter((d) => d?.month)
        .sort((a, b) => (a.month < b.month ? -1 : 1))
    : [];

  const currentYM = new Date().toISOString().slice(0, 7); // "YYYY-MM"

  // If no data yet, render the section header with an empty state message.
  if (sorted.length === 0) {
    return (
      <>
        <SectionHeader>Platform Usage</SectionHeader>
        <Box sx={CARD_SX}>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.35)', lineHeight: 1.5 }}>
            No data yet.
          </Typography>
        </Box>
      </>
    );
  }
  // If the current month has no entry yet, the gauge must show 0 — never
  // fall back to the most recent prior month, which would silently report
  // last month's totals under a "Generations This Month" header.
  const currentMonth = sorted.find((d) => d.month === currentYM) || null;

  const successfulThisMonth = currentMonth?.successful ?? 0;
  const failedThisMonth = currentMonth?.failed ?? 0;
  const hasLimit =
    monthlyGenerationLimit != null &&
    Number.isFinite(Number(monthlyGenerationLimit)) &&
    Number(monthlyGenerationLimit) > 0;
  const limitNum = hasLimit ? Number(monthlyGenerationLimit) : null;

  const usagePct =
    hasLimit ? Math.min(100, Math.round((successfulThisMonth / limitNum) * 100)) : null;

  // Chart: last 6 months
  const chartData = sorted.slice(-6).map((d) => ({
    month: monthLabel(d.month),
    successful: d.successful ?? 0,
    failed: d.failed ?? 0,
  }));

  return (
    <>
      {/* Section header */}
      <SectionHeader>Platform Usage</SectionHeader>

      {/* 3A — Usage gauge card */}
      <Box sx={CARD_SX}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: { xs: 1, sm: 1.25 } }}>
          <Typography
            sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'rgba(0,0,0,0.5)' }}
          >
            Generations This Month
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: '1.375rem', sm: '1.625rem' },
                fontWeight: 600,
                color: 'rgba(0,0,0,0.88)',
                lineHeight: 1,
                letterSpacing: '-0.025em',
              }}
            >
              {successfulThisMonth.toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.4)', mt: 0.3, lineHeight: 1.35 }}>
              {hasLimit
                ? `of ${limitNum.toLocaleString()} this calendar month`
                : 'generated this calendar month'}
              {' '}
              — not your date range above
            </Typography>
          </Box>
          {/* Circular ring when limit is set — reuse inline SVG pattern from submissions card */}
          {hasLimit && usagePct != null && (
            <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
              <svg
                width={54}
                height={54}
                style={{ display: 'block', transform: 'rotate(-90deg)' }}
                aria-hidden="true"
              >
                <circle
                  cx={27} cy={27} r={23.25}
                  fill="none"
                  stroke="rgba(0,0,0,0.07)"
                  strokeWidth={3.5}
                />
                <circle
                  cx={27} cy={27} r={23.25}
                  fill="none"
                  stroke={usagePct >= 90 ? ANALYTICS_RED : 'rgba(0,0,0,0.38)'}
                  strokeWidth={3.5}
                  strokeDasharray={2 * Math.PI * 23.25}
                  strokeDashoffset={2 * Math.PI * 23.25 * (1 - usagePct / 100)}
                  strokeLinecap="round"
                />
              </svg>
              <Box
                sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: usagePct >= 90 ? ANALYTICS_RED : 'rgba(0,0,0,0.72)',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {usagePct}%
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>

        {failedThisMonth > 0 && (
          <Typography
            sx={{
              mt: { xs: 1, sm: 1.25 },
              fontSize: '0.68rem',
              color: 'rgba(0,0,0,0.38)',
              lineHeight: 1.4,
            }}
          >
            {failedThisMonth} failed this month
          </Typography>
        )}
      </Box>

      {/* 3B — Monthly trend chart (only when ≥2 months of data) */}
      {chartData.length >= 2 && (
        <Box
          sx={{
            ...CARD_SX,
            mt: { xs: 1.5, sm: 2 },
            pb: { xs: 1, sm: 1.25 },
          }}
        >
          <Typography
            sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'rgba(0,0,0,0.5)', mb: { xs: 1.5, sm: 2 } }}
          >
            Monthly Trend
          </Typography>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart
              data={chartData}
              margin={{ top: 0, right: 4, left: -20, bottom: 0 }}
              barSize={14}
              barCategoryGap="35%"
            >
              <XAxis
                dataKey="month"
                tick={{ ...ANALYTICS_CHART_TICK, fontSize: 11, fill: 'rgba(0,0,0,0.4)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={ANALYTICS_CHART_TICK}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={36}
              />
              <Tooltip
                content={<UsageTooltip />}
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
              />
              {/* Successful bar — primary dark */}
              <Bar dataKey="successful" radius={[3, 3, 0, 0]} fill="rgba(0,0,0,0.75)">
                {chartData.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={entry.month === monthLabel(currentYM) ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.22)'}
                  />
                ))}
              </Bar>
              {/* Failed bar — muted red, only rendered when any failures exist */}
              {chartData.some((d) => d.failed > 0) && (
                <Bar
                  dataKey="failed"
                  radius={[3, 3, 0, 0]}
                  fill={analyticsRedAlpha(0.25)}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
          <Typography
            sx={{ fontSize: '0.65rem', color: 'rgba(0,0,0,0.28)', mt: 0.5, lineHeight: 1.4 }}
          >
            Successful generations · last {chartData.length} months
            {chartData.some((d) => d.failed > 0) && ' · red = failed'}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default PlatformUsageSection;
